/**
 * /comparisons/linear-docs — "Draftlize vs Linear Docs for PMs"
 * comparison. Targets PMs evaluating Linear for product docs vs
 * dedicated thinking substrate.
 */
import { MarketingShell } from '../MarketingShell';

export function LinearDocs() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/comparisons/linear-docs"
      documentTitle="Linear Alternative for the Thinking Phase: Draftlize vs Linear Docs · Draftlize"
      documentDescription="Linear Docs is great once work is an issue. Draftlize is the Linear alternative for the messy thinking phase before that — where AI agents help shape product decisions that don't drift. Compared for PMs."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Comparison · vs Linear Docs</span>
          <h1
            className="serif"
            style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}
          >
            Draftlize vs Linear Docs:<br/>
            <em>thinking phase vs shipping phase.</em>
          </h1>
          <p className="lede">
            Linear is the best issue tracker / project tool that exists. Linear Docs extended that to ship-adjacent documentation. But the *thinking* before something becomes an issue — the messy ideation, the rejected alternatives, the rationale — lives somewhere else. Draftlize is the substrate for that phase.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">Different phases, <em>different shapes.</em></h2>
          </div>
          <p>You can run both. Most PMs who use Draftlize also use Linear — Draftlize for the upstream thinking, Linear for the downstream tracking.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Linear Docs</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Issue + project + cycle tracking</td><td className="no">—</td><td className="yes">excellent</td></tr>
            <tr><td>Real-time team collaboration</td><td className="no">solo first</td><td className="yes">native</td></tr>
            <tr><td>AI agents that read decisions as context</td><td className="ours yes">native</td><td className="partial">Linear AI summaries</td></tr>
            <tr><td>AI agents can write structured updates back</td><td className="ours yes">native</td><td className="no">—</td></tr>
            <tr><td>Stale propagation when a decision changes</td><td className="ours yes">native</td><td className="no">—</td></tr>
            <tr><td>Card-level history + provenance</td><td className="ours yes">every edit, author, source</td><td className="yes">doc history</td></tr>
            <tr><td>Claude Code / Cursor / MCP integration</td><td className="ours yes">native</td><td className="no">—</td></tr>
            <tr><td>Spec-status drift detection (built → drift_detected)</td><td className="ours yes">native</td><td className="no">—</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Where <em>Linear wins.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Shipped work tracking</h3>
            <p>Issues, cycles, projects, milestones — Linear is the best execution layer that exists. Draftlize isn't trying to track issues; it's tracking the thinking that *becomes* issues.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Team workflow</h3>
            <p>Linear is built for product teams of 5-100. Real-time, fast, deep integrations with GitHub, Slack, Figma. Draftlize is solo-first; multi-user is on the roadmap but not v1.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Integrations with engineering tooling</h3>
            <p>PR linking, deploys, sentry integration — Linear has the engineering side covered. Draftlize has Claude Code / Cursor / MCP, which serves AI-assisted spec→build flows but not traditional dev workflows.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Where <em>Draftlize wins.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">The thinking before an issue exists</h3>
            <p>Linear assumes you've already decided to build the thing. Draftlize is for the upstream phase — the "should we build it" / "what should it do" / "what does it depend on" thinking that produces issues for Linear later.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Rejected alternatives stay visible</h3>
            <p>In Linear, rejected ideas vanish. In Draftlize, they become <code>dead_end</code> kind cards — agent sees "this path was explored and abandoned" instead of re-suggesting it three sprints later.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI agents that update specs in place</h3>
            <p>Linear doesn't currently have an AI that can edit specs based on conversation. Draftlize does — and when you implement a spec (mark it <code>built</code>), edits later auto-flip status to <code>drift_detected</code> so you know what's gone out of sync with the code.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Linear: what we're building. Draftlize: why we decided to build it, and what changes when that decision moves.
        </blockquote>
        <cite>The clean way to run both: Draftlize cards graduate to Linear issues when they're concrete enough to ship.</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Add Draftlize <em>upstream of Linear.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            New accounts start with $5 free, pay only for what you use, balance never expires. Keep Linear for shipping. Add Draftlize for the thinking that decides what gets shipped.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
